import { Helper } from "../../helpers/helper";
import { TelerikUtility } from "../telerik.utility";

/**
 * Shows or hides a target field based on whether the value of another field matches one of a set of expected values, and toggles the target field's validation accordingly.
 * @param jValueElement The element whose current value is checked against `expectedValues`.
 * @param jTargetElement The element to show or hide based on the comparison result.
 * @param expectedValues The set of values that, when matched, cause the target field to be shown.
 * @param ignoreRequiredCheck When `true`, toggles validators even if the target field was not originally required. Defaults to `false`.
 * @param fieldContainerSelector The selector used to find the field's containing wrapper element for show/hide and validator lookup. Defaults to `".PanelField"`.
 * @returns `true` if the target field was shown, otherwise `false`.
 */
function toggleByExpectedValues(jValueElement: JQuery, jTargetElement: JQuery, expectedValues: any[], ignoreRequiredCheck = false, fieldContainerSelector: any = ".PanelField") {
    let elementValue = getValueByType(jValueElement);
    let isShow = expectedValues.includes(elementValue);

    showField(jTargetElement, isShow, isShow, ignoreRequiredCheck, fieldContainerSelector);
    return isShow;
}

/**
 * Shows or hides a field's containing wrapper element, resetting the field's value when hiding it, and optionally enables or disables its validators.
 * @param jElement The field element whose container should be shown or hidden.
 * @param showField When `true`, shows the field's container; when `false`, resets the field's value and hides the container.
 * @param enableValidation When not `null`, enables or disables the field's validators to match. Pass `null` to leave validator state unchanged.
 * @param ignoreRequiredCheck When `true`, toggles validators even if the field was not originally required. Defaults to `false`.
 * @param fieldContainerSelector The selector used to find the field's containing wrapper element. Defaults to `".PanelField"`.
 */
function showField(jElement: JQuery, showField: boolean, enableValidation: boolean, ignoreRequiredCheck = false, fieldContainerSelector: any = ".PanelField") {
    // evaluate the container for the field to hide
    let jElementContainer = jElement.closest(fieldContainerSelector);
    if (showField) {
        jElementContainer.show();
    } else {
        // reset the value on hide
        resetValue(jElement);
        jElementContainer.hide();
    }

    if (enableValidation != null) {
        toggleValidators(jElement, enableValidation, ignoreRequiredCheck, fieldContainerSelector);
    }
}

/**
 * Enables or disables the ASP.NET validators associated with a field (found within its container element), toggling the "Required" CSS class on its label and the `required` data/attribute state to match.
 *
 * Validators are only toggled if the field was originally required (or `ignoreRequiredCheck` is `true`). Because `required` is itself updated by this method, the field's initial required state is captured once in the `originallyRequired` data value and used for all later calls.
 * @param jElement The field element whose validators should be toggled. Its own `data()` is checked for a `required` flag first, falling back to its parent's `data()` (e.g. for date fields inside wrappers). When found on the parent, `originallyRequired` and `required` are stored on the parent.
 * @param enable When `true`, enables the field's validators, marks its label as required and sets `required` to `true`; when `false`, disables them, removes the required styling and sets `required` to `false`.
 * @param ignoreRequiredCheck When `true`, toggles validators even if the field was not originally required. Defaults to `false`.
 * @param fieldContainerSelector The selector used to find the field's containing wrapper element, used to locate its validators and label. Defaults to `".PanelField"`.
 */
function toggleValidators(jElement: JQuery, enable: boolean, ignoreRequiredCheck = false, fieldContainerSelector: any = ".PanelField") {
    // the required flag may not be retrievable from the element directly and might be on the parent element (i.e. for dates with wrappers)
    let isDataOnParentElement = false;
    let dataObject = jElement.data();
    if (dataObject.required == null) {
        dataObject = jElement.parent().data();
        if (dataObject.required != null) {
            isDataOnParentElement = true;
        }
    }
    let jElementToUpdate = isDataOnParentElement ? jElement.parent() : jElement;

    // toggleByExpectedValues uses a single value for both show/hide and validation, so it asks to enable validators for every field it shows, even ones that were never required
    // since data.required is now toggled by this method, it can no longer tell us whether the field was required to begin with, so we capture that once as originallyRequired
    // validators are then only toggled if the field was originally required (or if the check is being ignored)
    if (dataObject.originallyRequired == null) {
        dataObject.originallyRequired = dataObject.required || false;
        jElementToUpdate.data("originallyRequired", dataObject.originallyRequired);
    }

    if (dataObject.originallyRequired || ignoreRequiredCheck) {
        // we have to get validators by finding the closest field container element and using that to get all ValidationError classed elements
        let validators = jElement.closest(fieldContainerSelector).find(".ValidationError");
        validators.each(function () {
            // toggle the validator and keep its required attribute/data in sync
            window.ValidatorEnable(this, enable);
            jQuery(this).attr("data-required", enable.toString());
            jQuery(this).data("required", enable);
        });

        if (validators.length > 0) {
            // find the nearest label and toggle the required class
            let jLabel = jElement.closest(fieldContainerSelector).find("label");
            if (enable) {
                jLabel.addClass("Required");
            } else {
                jLabel.removeClass("Required");
            }

            // keep the field's own required attribute/data in sync (on the parent if that is where it was found)
            jElementToUpdate.attr("data-required", enable.toString());
            jElementToUpdate.data("required", enable);
        }
    }
}

/**
 * Clears a field's value, using the Telerik control's `clear`/`clearSelection` API when the element is a Telerik RadWebControl, or otherwise clearing the underlying jQuery element's value/checked state directly.
 * @param jElement The field element whose value should be reset.
 */
function resetValue(jElement: JQuery) {
    // ensure the element is not a telerik control
    let elementId = jElement.attr("id");
    let tElement: Telerik.Web.UI.RadWebControl = $find(elementId);
    if (tElement == null) {
        jElement.val("");
        
        // covers checkboxes
        jElement.prop("checked", false);
    } else {
        // clear the value of the telerik control based on the method available
        if (tElement["clear"] != null) {
            tElement["clear"]();   
        } else if (tElement["clearSelection"] != null) {
            tElement["clearSelection"]();
        }
    }
}

/**
 * Sets a field's value to the given default, but only when the field is currently empty.
 * @param elementSelector A jQuery selector (or element) identifying the field to default.
 * @param defaultValue The value to apply if the field currently has no value.
 */
function setDefaultValue(elementSelector: any, defaultValue: any) {
    let jElement = jQuery(elementSelector);
    let value = getValueByType(jElement);
    if (Helper.isStringNullOrEmpty(value)) {
        setValueByType(jElement, defaultValue);
    }
}

/**
 * Reads the current value of a field, handling Telerik text controls, plain text/select inputs, radio button groups (returning the checked option's value), and checkboxes (returning the checked state).
 * @param jElements The field element(s) to read the value from.
 * @returns The field's current value: a string/other value for text-like and radio fields, or a boolean for checkboxes.
 */
function getValueByType(jElements: JQuery<HTMLElement>) {
    let value: any;

    if (isFormElementTypeText(jElements)) {
        // ensure whether text control is Telerik or not
        let tElement: Telerik.Web.UI.RadWebControl = $find(jElements.attr("id"));
        if (tElement != null) {
            value = TelerikUtility.getTelerikControlValue(tElement);
        } else {
            value = jElements.val();
        }
    } else if (isFormElementTypeRadio(jElements)) {
        // this could possibly involve more than one radio button, filter out the unchecked ones and get the value
        value = jElements.filter(":checked").val();
    } else {
        value = jElements.prop("checked");
    }

    return value;
}

/**
 * Sets the value of a field, handling Telerik text controls, plain text/select inputs, radio button groups (checking the option matching the given value), and checkboxes (setting the checked state).
 * @param jElements The field element(s) to set the value on.
 * @param newValue The value to apply.
 */
function setValueByType(jElements: JQuery<HTMLElement>, newValue: any) {
    // this works for a select tag too because it will be null
    if (isFormElementTypeText(jElements)) {
        // ensure whether text control is Telerik or not
        let tElement: Telerik.Web.UI.RadWebControl = $find(jElements.attr("id"));
        if (tElement != null) {
            TelerikUtility.setTelerikControlValue(tElement, newValue);
        } else {
            jElements.val(newValue);
        }
    } else if (isFormElementTypeRadio(jElements)) {
        // this could possible involve more than one radio element, select the one with the matching value
        jElements.filter(`[value=${newValue}]`).prop("checked", true);
    } else {
        jElements.prop("checked", newValue);
    }
}

/**
 * Determines whether a field element is a text-like input, i.e. not a checkbox or radio button.
 * @param jElement The field element to check.
 * @returns `true` if the element's `type` attribute is neither `"checkbox"` nor `"radio"`.
 */
function isFormElementTypeText(jElement: JQuery<HTMLElement>): boolean {
    let type = jElement.attr("type");
    return !["checkbox", "radio"].includes(type);
}

/**
 * Determines whether a field element is a radio button.
 * @param jElement The field element to check.
 * @returns `true` if the element's `type` attribute is `"radio"`.
 */
function isFormElementTypeRadio(jElement: JQuery<HTMLElement>): boolean {
    let type = jElement.attr("type");
    return type == "radio";
}

export {
    setDefaultValue,
    getValueByType,
    setValueByType,
    resetValue,
    toggleByExpectedValues,
    toggleValidators,
    showField,
};
